//#region IMPORT
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { showSnackbar } from '../../../../Reducer/SnackbarReducer';
import { closeLoading, showLoading } from '../../../../Reducer/LoadingReducer';
import { createInitial } from '../../../../util';
import Commons from '../../../../Services/Common/Common';
import { apiRequest } from '../../../../Services/Common/ApiRequest';
import Global from '../../../../Services/Common/Global';
import { DAT_BASIC } from './index';
import { DAT_BASIC_Schema } from './DAT_BASIC_Schema';
import {
  DAT_BASIC_MIGRATION_MODE,
  GetAllMigratableBulkLabels,
  GetBudBasicDeleteTargets,
  NeedsDepMapping,
  ResolveDefaultTabKey,
  ShowsBudBasicDelete,
} from './DAT_BASIC_MigrationPages';
import { useCopy1, useCopy2, useDelete1 } from './DAT_BASIC_QUERY';
//#endregion

const mapDepLastRows = (rows) => {
  if (!Array.isArray(rows)) return [];
  return rows
    .map((row) => {
      const depCd = String(row?.depCd ?? row?.DepCd ?? '').trim();
      const depNm = String(
        row?.depNmLast ?? row?.DepNmLast ?? row?.depNm ?? row?.DepNm ?? ''
      ).trim();
      if (!depCd) return null;
      return { value: depCd, name: depNm || depCd };
    })
    .filter(Boolean);
};

const formatCustDisplay = (custCd, custNm) => {
  const code = String(custCd ?? '').trim();
  if (!code) return '미입력';
  const name = String(custNm ?? '').trim();
  return name ? `${name} (${code})` : code;
};

const buildCoList = (companyInfo) => {
  if (!Array.isArray(companyInfo)) return [];
  return companyInfo
    .map((item) => ({
      value: String(item?.coCd ?? item?.CoCd ?? '').trim(),
      name: String(item?.coNm ?? item?.CoNm ?? '').trim(),
    }))
    .filter((item) => item.value);
};

const filterCustListByCo = (custInfo, coCd) => {
  const co = String(coCd ?? '').trim();
  if (!co || !Array.isArray(custInfo)) return [];
  return custInfo
    .filter((item) => String(item?.coCd ?? item?.CoCd ?? '').trim() === co)
    .map((item) => ({
      value: String(item?.custCd ?? item?.CustCd ?? '').trim(),
      name: String(item?.custNm ?? item?.CustNm ?? '').trim(),
    }))
    .filter((item) => item.value);
};

function DAT_BASICF() {
  const dispatch = useDispatch();
  const { userInfo, custInfo, companyInfo } = useSelector(
    (state) => state.BaseInfo
  );

  const [inputs, setInputs] = useState(() =>
    createInitial(DAT_BASIC_Schema)
  );
  const [sourceDepOptions, setSourceDepOptions] = useState([]);
  const [targetDepOptions, setTargetDepOptions] = useState([]);
  const [depLoading, setDepLoading] = useState(false);

  const coList = useMemo(() => buildCoList(companyInfo), [companyInfo]);
  const sourceCustList = useMemo(
    () => filterCustListByCo(custInfo, inputs.sourceCoCd),
    [custInfo, inputs.sourceCoCd]
  );
  const targetCustList = useMemo(
    () => filterCustListByCo(custInfo, inputs.targetCoCd),
    [custInfo, inputs.targetCoCd]
  );
  const targetCustDisplay = useMemo(() => {
    const item = targetCustList.find(
      (row) => row.value === inputs.targetCustCd
    );
    return formatCustDisplay(inputs.targetCustCd, item?.name);
  }, [inputs.targetCustCd, targetCustList]);

  const { runAsync: Copy1Async } = useCopy1();
  const { runAsync: Copy2Async } = useCopy2();
  const { runAsync: Delete1Async } = useDelete1();

  const showSamDepMapping = NeedsDepMapping(inputs);
  const showBudBasicDelete = ShowsBudBasicDelete(inputs);
  const budDeleteTargets = GetBudBasicDeleteTargets(inputs);
  const migratableBulkLabels = useMemo(
    () => GetAllMigratableBulkLabels(),
    []
  );

  const fetchDepOptions = useCallback(async (custCd) => {
    const trimmed = String(custCd ?? '').trim();
    if (!trimmed) return [];

    const params = {
      CustCd: Commons.EncryptAES(trimmed) || '',
      SearchText: Commons.EncryptAES('') || '',
    };

    const result = await apiRequest(
      'post',
      Global.urls.Utility.getDepLast,
      params
    );
    const data = result?.data;
    if (!data || data.length === 0 || data[0]?.type === 'Error') {
      return [];
    }
    return mapDepLastRows(data);
  }, []);

  useEffect(() => {
    if (!showSamDepMapping) {
      setSourceDepOptions([]);
      setTargetDepOptions([]);
      return;
    }

    let cancelled = false;
    const load = async () => {
      setDepLoading(true);
      try {
        const [sourceOpts, targetOpts] = await Promise.all([
          fetchDepOptions(inputs.sourceCustCd),
          fetchDepOptions(inputs.targetCustCd),
        ]);
        if (cancelled) return;
        setSourceDepOptions(sourceOpts);
        setTargetDepOptions(targetOpts);
      } catch {
        if (!cancelled) {
          setSourceDepOptions([]);
          setTargetDepOptions([]);
        }
      } finally {
        if (!cancelled) setDepLoading(false);
      }
    };

    void load();
    return () => {
      cancelled = true;
    };
  }, [
    showSamDepMapping,
    inputs.sourceCustCd,
    inputs.targetCustCd,
    fetchDepOptions,
  ]);

  const HandleInputChange = (field) => (e) => {
    const value = e?.target?.value ?? e ?? '';
    setInputs((prev) => {
      const next = {
        ...prev,
        [field]: String(value).trim(),
      };
      if (field === 'sourceCustCd') {
        next.sourceDepCd = '';
      }
      if (field === 'targetCustCd') {
        next.targetDepCd = '';
      }
      return next;
    });
  };

  const HandleSourceCoChange = (coCd) => {
    setInputs((prev) => ({
      ...prev,
      sourceCoCd: String(coCd ?? '').trim(),
      sourceCustCd: '',
      sourceDepCd: '',
    }));
  };

  const HandleTargetCoChange = (coCd) => {
    setInputs((prev) => ({
      ...prev,
      targetCoCd: String(coCd ?? '').trim(),
      targetCustCd: '',
      targetDepCd: '',
    }));
  };

  const HandleMigrationModeChange = (mode) => {
    setInputs((prev) => ({
      ...prev,
      migrationMode: mode,
      proId: mode === DAT_BASIC_MIGRATION_MODE.PAGE ? prev.proId : '',
      tabKey: '',
      sourceDepCd: '',
      targetDepCd: '',
    }));
  };

  const HandleProIdChange = (proId) => {
    const normalized = String(proId ?? '').trim();
    setInputs((prev) => ({
      ...prev,
      proId: normalized,
      tabKey: ResolveDefaultTabKey(normalized),
      sourceDepCd: '',
      targetDepCd: '',
    }));
  };

  const HandleTabKeyChange = (tabKey) => {
    setInputs((prev) => ({
      ...prev,
      tabKey: String(tabKey ?? '').trim(),
      sourceDepCd: '',
      targetDepCd: '',
    }));
  };

  const ValidateCopyCodes = () => {
    const { sourceCoCd, sourceCustCd, targetCoCd, targetCustCd } = inputs;

    if (!sourceCoCd) {
      dispatch(
        showSnackbar({
          message: '원본 회사를 선택하세요.',
          type: 'warning',
        })
      );
      return false;
    }
    if (!sourceCustCd) {
      dispatch(
        showSnackbar({
          message: '원본 사업장을 선택하세요.',
          type: 'warning',
        })
      );
      return false;
    }
    if (!targetCoCd) {
      dispatch(
        showSnackbar({
          message: '대상 회사를 선택하세요.',
          type: 'warning',
        })
      );
      return false;
    }
    if (!targetCustCd) {
      dispatch(
        showSnackbar({
          message: '대상 사업장을 선택하세요.',
          type: 'warning',
        })
      );
      return false;
    }
    if (sourceCoCd === targetCoCd && sourceCustCd === targetCustCd) {
      dispatch(
        showSnackbar({
          message: '원본과 대상 코드가 동일합니다.',
          type: 'warning',
        })
      );
      return false;
    }
    return true;
  };

  const ValidateSamDepMapping = () => {
    if (!NeedsDepMapping(inputs)) return true;

    if (!String(inputs.sourceDepCd ?? '').trim()) {
      dispatch(
        showSnackbar({
          message: '원본 부서를 선택하세요.',
          type: 'warning',
        })
      );
      return false;
    }
    if (!String(inputs.targetDepCd ?? '').trim()) {
      dispatch(
        showSnackbar({
          message: '대상 부서를 선택하세요.',
          type: 'warning',
        })
      );
      return false;
    }
    if (inputs.sourceDepCd === inputs.targetDepCd && inputs.sourceCustCd === inputs.targetCustCd) {
      dispatch(
        showSnackbar({
          message: '동일 사업장·부서로는 이관할 수 없습니다.',
          type: 'warning',
        })
      );
      return false;
    }
    return true;
  };

  const RunCopyApi = async (runAsync, successMessage, failMessage) => {
    dispatch(showLoading());
    try {
      const result = await runAsync({
        ...inputs,
        userInfo,
      });

      const isSuccess = result?.isSuccess ?? result?.IsSuccess ?? false;
      const message =
        result?.message ??
        result?.Message ??
        (isSuccess ? successMessage : failMessage);

      dispatch(
        showSnackbar({
          message,
          type: isSuccess ? 'success' : 'error',
        })
      );
    } catch (e) {
      dispatch(
        showSnackbar({
          message: e?.message || '데이터 이관 중 오류가 발생했습니다.',
          type: 'error',
        })
      );
    } finally {
      dispatch(closeLoading());
    }
  };

  /** 이관 가능 전체 일괄 이관 (부서 매핑 불필요 항목) */
  const CopyLogicAll = async () => {
    if (!ValidateCopyCodes()) return;

    const labels = migratableBulkLabels;
    if (!labels.length) {
      dispatch(
        showSnackbar({
          message: '일괄 이관 대상이 없습니다.',
          type: 'warning',
        })
      );
      return;
    }

    const confirmed = window.confirm(
      `아래 ${labels.length}개 항목을 한 번에 이관합니다.\n\n` +
        `${labels.map((label) => `· ${label}`).join('\n')}\n\n` +
        '※ 작업계획서 등 부서 매핑이 필요한 항목은 포함되지 않습니다.\n' +
        '※ 이미 이관된 데이터는 중복 복사되지 않을 수 있습니다.\n\n' +
        '계속하시겠습니까?'
    );
    if (!confirmed) return;

    await RunCopyApi(
      Copy1Async,
      '이관 가능 전체 데이터 이관이 완료되었습니다.',
      '일부 항목 이관에 실패했습니다. 상세 메시지를 확인하세요.'
    );
  };

  /** 선택 메뉴·탭 이관 */
  const CopyLogic2 = async () => {
    if (!ValidateCopyCodes()) return;

    if (!String(inputs.proId ?? '').trim()) {
      dispatch(
        showSnackbar({
          message: '이관할 페이지를 선택하세요.',
          type: 'warning',
        })
      );
      return;
    }

    if (!ValidateSamDepMapping()) return;

    await RunCopyApi(
      Copy2Async,
      '선택한 범위의 데이터 이관이 완료되었습니다.',
      '선택한 범위의 데이터 이관에 실패했습니다.'
    );
  };

  const HandleCopyClick = () => {
    void CopyLogic2();
  };

  const ValidateTargetCustCd = () => {
    if (!String(inputs.targetCustCd ?? '').trim()) {
      dispatch(
        showSnackbar({
          message: '대상 사업장을 선택하세요.',
          type: 'warning',
        })
      );
      return false;
    }
    return true;
  };

  const HandleDeleteClick = async () => {
    if (!ValidateTargetCustCd()) return;

    const targetLabel = targetCustDisplay;
    const targetLabels = budDeleteTargets.map((t) => t.label).join(', ');
    const confirmed = window.confirm(
      `대상 사업장 ${targetLabel}의 아래 데이터를 삭제합니다.\n` +
        `${targetLabels}\n\n` +
        '삭제 후 복구할 수 없습니다. 계속하시겠습니까?'
    );
    if (!confirmed) return;

    dispatch(showLoading());
    try {
      const result = await Delete1Async({ ...inputs, userInfo });
      const isSuccess = result?.isSuccess ?? result?.IsSuccess ?? false;
      const message =
        result?.message ??
        result?.Message ??
        (isSuccess ? '대상 예산 데이터 삭제가 완료되었습니다.' : '삭제에 실패했습니다.');

      dispatch(
        showSnackbar({
          message,
          type: isSuccess ? 'success' : 'error',
        })
      );
    } catch (e) {
      dispatch(
        showSnackbar({
          message: e?.message || '데이터 삭제 중 오류가 발생했습니다.',
          type: 'error',
        })
      );
    } finally {
      dispatch(closeLoading());
    }
  };

  return (
    <div className="wrap">
      <DAT_BASIC
        inputs={inputs}
        showSamDepMapping={showSamDepMapping}
        coList={coList}
        sourceCustList={sourceCustList}
        targetCustList={targetCustList}
        sourceDepOptions={sourceDepOptions}
        targetDepOptions={targetDepOptions}
        depLoading={depLoading}
        HandleInputChange={HandleInputChange}
        HandleSourceCoChange={HandleSourceCoChange}
        HandleTargetCoChange={HandleTargetCoChange}
        HandleMigrationModeChange={HandleMigrationModeChange}
        HandleProIdChange={HandleProIdChange}
        HandleTabKeyChange={HandleTabKeyChange}
        HandleCopyClick={HandleCopyClick}
        HandleCopyAllClick={() => {
          void CopyLogicAll();
        }}
        migratableBulkCount={migratableBulkLabels.length}
        migratableBulkLabels={migratableBulkLabels}
        showBudBasicDelete={showBudBasicDelete}
        budDeleteTargets={budDeleteTargets}
        targetCustDisplay={targetCustDisplay}
        HandleDeleteClick={HandleDeleteClick}
      />
    </div>
  );
}

export default DAT_BASICF;
